import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import LoadingComponent from './customer/loading';
import axios from 'axios';

const ANALYTICS_TOKEN_ENDPOINT = process.env.REACT_APP_ANALYTICS_TOKEN_URL || 'https://api.insightos.co.uk/api/datatoken';
const ANALYTICS_REFRESH_BUFFER_MS = 60 * 1000;
const ANALYTICS_MIN_REFRESH_MS = 15 * 1000;
const ANALYTICS_FETCH_RETRY_MS = 10 * 1000;

const decodeJwtExpiry = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized));
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    console.warn('Failed to decode analytics token expiry', error);
    return null;
  }
};

const pickAnalyticsTokenFromResponse = (data = {}) => data.analyticsToken || data.token || data?.data?.analyticsToken || null;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
  const [analyticsToken, setAnalyticsToken] = useState(null);
    const [isVerifying, setIsVerifying] = useState(true);
  const analyticsRefreshTimeoutRef = useRef(null);
  const lastAnalyticsFetchAttemptRef = useRef(0);
  const refreshAnalyticsTokenRef = useRef(null);

  const clearAnalyticsRefreshTimer = useCallback(() => {
    if (analyticsRefreshTimeoutRef.current) {
      clearTimeout(analyticsRefreshTimeoutRef.current);
      analyticsRefreshTimeoutRef.current = null;
    }
  }, []);

  const clearAnalyticsTokenState = useCallback(() => {
    setAnalyticsToken(null);
    clearAnalyticsRefreshTimer();
  }, [clearAnalyticsRefreshTimer]);

  const handleAnalyticsToken = useCallback(
    (token) => {
      if (!token) {
        clearAnalyticsTokenState();
        return null;
      }

      const expiresAt = decodeJwtExpiry(token);
      setAnalyticsToken(token);
      return expiresAt;
    },
    [clearAnalyticsTokenState],
  );

  const scheduleAnalyticsRefresh = useCallback(
    (expiresAtMs) => {
      clearAnalyticsRefreshTimer();
      if (!expiresAtMs) return;

      const refreshDelay = Math.max(expiresAtMs - Date.now() - ANALYTICS_REFRESH_BUFFER_MS, ANALYTICS_MIN_REFRESH_MS);
      analyticsRefreshTimeoutRef.current = setTimeout(() => {
        refreshAnalyticsTokenRef.current?.('auto');
      }, refreshDelay);
    },
    [clearAnalyticsRefreshTimer],
  );

  const refreshAnalyticsToken = useCallback(
    async (reason = 'manual') => {
      try {
        const response = await axios.get(ANALYTICS_TOKEN_ENDPOINT, {
          withCredentials: true,
        });

        const token = pickAnalyticsTokenFromResponse(response.data);
        if (!token) {
          throw new Error('Missing analytics token in response');
        }

        const expiresAt = handleAnalyticsToken(token);
        if (expiresAt) {
          scheduleAnalyticsRefresh(expiresAt);
        }
        return token;
      } catch (error) {
        console.error(`Failed to refresh analytics token (${reason})`, error);
        clearAnalyticsTokenState();
        return null;
      }
    },
    [handleAnalyticsToken, scheduleAnalyticsRefresh, clearAnalyticsTokenState],
  );

  useEffect(() => {
    refreshAnalyticsTokenRef.current = refreshAnalyticsToken;
    return () => {
      if (refreshAnalyticsTokenRef.current === refreshAnalyticsToken) {
        refreshAnalyticsTokenRef.current = null;
      }
    };
  }, [refreshAnalyticsToken]);

  useEffect(() => () => clearAnalyticsRefreshTimer(), [clearAnalyticsRefreshTimer]);

    useEffect(() => {
        const verifyToken = async () => {
            try {
              const response = await fetch('https://api.insightos.co.uk/admin/auth', {
                method: 'GET',
                credentials: 'include', // Important to send cookies like the JWT
              });
          
              const data = await response.json();
          
              if (data.success) {
                console.log('Token is valid:', data.user);
                setUser(data.user);
              } else {
                console.log('Invalid or missing token');
                setUser(null);
                clearAnalyticsTokenState();
                if (window.location.pathname !== '/') {
                  window.location.href = '/';
                }
              }
            } catch (error) {
              console.error('Auth verification failed:', error);
              setUser(null);
              clearAnalyticsTokenState();
              if (window.location.pathname !== '/') {
                window.location.href = '/';
              }
            } finally {
              setIsVerifying(false);
            }
          };
          
        verifyToken();
      }
      , [clearAnalyticsTokenState]);      

    useEffect(() => {
      if (!user) {
        lastAnalyticsFetchAttemptRef.current = 0;
        clearAnalyticsTokenState();
        return;
      }

      if (!analyticsToken) {
        const now = Date.now();
        if (now - lastAnalyticsFetchAttemptRef.current >= ANALYTICS_FETCH_RETRY_MS) {
          lastAnalyticsFetchAttemptRef.current = now;
          refreshAnalyticsToken('bootstrap');
        }
      }
    }, [user, analyticsToken, refreshAnalyticsToken, clearAnalyticsTokenState]);

    if (isVerifying) {
        return <LoadingComponent />;
    }

    const authValue = user ? { ...user, analyticsToken, refreshAnalyticsToken } : null;

    return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}
